/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// This file is copy-pasted from the backstage repo, and edited as per our requirements.
// reference: https://github.com/backstage/backstage/blob/ab539cc72bb503618df55815f24c4ba933607460/plugins/techdocs/src/client.ts

import { CompoundEntityRef } from '@backstage/catalog-model';
import { Config } from '@backstage/config';
import {
    DiscoveryApi,
    FetchApi,
    IdentityApi,
} from '@backstage/core-plugin-api';
import { NotFoundError, ResponseError } from '@backstage/errors';
import {
    SyncResult,
    TechDocsApi,
    TechDocsEntityMetadata,
    TechDocsMetadata,
    TechDocsStorageApi, techdocsStorageApiRef,
} from '@backstage/plugin-techdocs-react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import {
    AnyApiFactory,
    configApiRef,
    createApiFactory,
    discoveryApiRef,
    fetchApiRef,
} from '@backstage/core-plugin-api';
import {techdocsApiRef} from "@backstage/plugin-techdocs-react";

/**
 * API to talk to `techdocs-backend`.
 *
 * @public
 */
export class TechDocsClient implements TechDocsApi {
    public configApi: Config;
    public discoveryApi: DiscoveryApi;
    private fetchApi: FetchApi;

    constructor(options: {
        configApi: Config;
        discoveryApi: DiscoveryApi;
        fetchApi: FetchApi;
    }) {
        this.configApi = options.configApi;
        this.discoveryApi = options.discoveryApi;
        this.fetchApi = options.fetchApi;
    }

    public async getCookie(): Promise<{ expiresAt: string }> {
        const apiOrigin = await this.getApiOrigin();
        const requestUrl = `${apiOrigin}/cookie`;
        const response = await this.fetchApi.fetch(`${requestUrl}`, {
            credentials: 'include',
        });
        if (!response.ok) {
            throw await ResponseError.fromResponse(response);
        }
        return await response.json();
    }

    async getApiOrigin(): Promise<string> {
        return await this.discoveryApi.getBaseUrl('techdocs');
    }

    /**
     * Retrieve TechDocs metadata.
     *
     * When docs are built, we generate a techdocs_metadata.json and store it along with the generated
     * static files. It includes necessary data about the docs site. This method requests techdocs-backend
     * which retrieves the TechDocs metadata.
     *
     * @param entityId - Object containing entity data like name, namespace, etc.
     */
    async getTechDocsMetadata(
        entityId: CompoundEntityRef,
    ): Promise<TechDocsMetadata> {
        const { kind, namespace, name } = entityId;

        const apiOrigin = await this.getApiOrigin();
        let requestUrl = `${apiOrigin}/metadata/techdocs/${namespace}/${kind}/${name}`;

        // in the preview mode, we fetch the techdocs_metadata.json file directly from the storage
        if (inPreviewMode()) {
            const storageUrl = await this.getStorageUrl();
            requestUrl = `${storageUrl}/_previews/${name}/${getPreviewBranchName()}/techdocs_metadata.json`;
        }
        const request = await this.fetchApi.fetch(`${requestUrl}`);
        if (!request.ok) {
            throw await ResponseError.fromResponse(request);
        }

        return await request.json();
    }

    /**
     * Retrieve metadata about an entity.
     *
     * This method requests techdocs-backend which uses the catalog APIs to respond with filtered
     * information required here.
     *
     * @param entityId - Object containing entity data like name, namespace, etc.
     */
    async getEntityMetadata(
        entityId: CompoundEntityRef,
    ): Promise<TechDocsEntityMetadata> {
        const { kind, namespace, name } = entityId;

        const apiOrigin = await this.getApiOrigin();
        const requestUrl = `${apiOrigin}/metadata/entity/${namespace}/${kind}/${name}`;

        if (inPreviewMode()) {
            // return available metadata, since there won't be any entity metadata in the preview mode.
            return {
                apiVersion: 'backstage.io/v1alpha1',
                kind: 'Component',
                metadata: {
                    name,
                    namespace,
                    kind,
                    title: name,
                },
            };
        }

        // This function is expected to fail, when not in preview mode and the docs site is not generated yet.

        const request = await this.fetchApi.fetch(`${requestUrl}`);
        if (!request.ok) {
            throw await ResponseError.fromResponse(request);
        }

        return await request.json();
    }

    // this change is to support the preview mode
    async getStorageUrl(): Promise<string> {
        return (
            this.configApi.getOptionalString('techdocs.storageUrl') ??
            `${await this.discoveryApi.getBaseUrl('techdocs')}/static/docs`
        );
    }
}

/**
 * API which talks to TechDocs storage to fetch files to render.
 *
 * @public
 */
export class TechDocsStorageClient implements TechDocsStorageApi {
    public configApi: Config;
    public discoveryApi: DiscoveryApi;
    private fetchApi: FetchApi;

    constructor(options: {
        configApi: Config;
        discoveryApi: DiscoveryApi;
        fetchApi: FetchApi;
        /** @deprecated identityApi is not needed any more */
        identityApi?: IdentityApi;
    }) {
        this.configApi = options.configApi;
        this.discoveryApi = options.discoveryApi;
        this.fetchApi = options.fetchApi;
    }

    async getApiOrigin(): Promise<string> {
        return await this.discoveryApi.getBaseUrl('techdocs');
    }

    async getStorageUrl(): Promise<string> {
        return (
            this.configApi.getOptionalString('techdocs.storageUrl') ??
            `${await this.discoveryApi.getBaseUrl('techdocs')}/static/docs`
        );
    }

    async getBuilder(): Promise<string> {
        return this.configApi.getOptionalString('techdocs.builder') || 'local';
    }

    /**
     * Fetch HTML content as text for an individual docs page in an entity's docs site.
     *
     * @param entityId - Object containing entity data like name, namespace, etc.
     * @param path - The unique path to an individual docs page e.g. overview/what-is-new
     * @returns HTML content of the docs page as string
     * @throws Throws error when the page is not found.
     */
    async getEntityDocs(
        entityId: CompoundEntityRef,
        path: string,
    ): Promise<string> {

        const { kind, namespace, name } = entityId;
        console.log(kind, namespace,name);

        const storageUrl = await this.getStorageUrl();
        let url = `${storageUrl}/${namespace}/${kind}/${name}/${path}`;

        // in the preview mode, we fetch the docs from `_previews/<repo-name>/<preview-branch-name>` directory in the storage
        if (inPreviewMode()) {
            url = `${storageUrl}/_previews/${name}/${getPreviewBranchName()}/${path}`;
        }

        const request = await this.fetchApi.fetch(
            `${url.endsWith('/') ? url : `${url}/`}index.html`,
        );

        let errorMessage = '';
        switch (request.status) {
            case 404:
                errorMessage = 'Page not found. ';
                // path is empty for the home page of an entity's docs site
                if (!path) {
                    // todo: update this error message
                    errorMessage +=
                        'This could be because there is no index.md file in the root of the docs directory of this repository.';
                }
                throw new NotFoundError(errorMessage);
            case 500:
                errorMessage =
                    'Could not generate documentation or an error in the TechDocs backend. ';
                throw new Error(errorMessage);
            default:
                // Do nothing
                break;
        }

        return request.text();
    }

    /**
     * Check if docs are on the latest version and trigger rebuild if not
     *
     * @param entityId - Object containing entity data like name, namespace, etc.
     * @param logHandler - Callback to receive log messages from the build process
     * @returns Whether documents are currently synchronized to newest version
     * @throws Throws error on error from sync endpoint in TechDocs Backend
     */
    async syncEntityDocs(
        entityId: CompoundEntityRef,
        logHandler: (line: string) => void = () => {},
    ): Promise<SyncResult> {
        const { kind, namespace, name } = entityId;

        const apiOrigin = await this.getApiOrigin();
        const url = `${apiOrigin}/sync/${namespace}/${kind}/${name}`;

        return new Promise((resolve, reject) => {
            // In the preview mode, we don't actually sync the docs
            // Check if we are in preview mode, then resolve with `updated`
            if (inPreviewMode()) {
                resolve("updated");
            }

            const ctrl = new AbortController();
            fetchEventSource(url, {
                fetch: this.fetchApi.fetch,
                signal: ctrl.signal,
                onmessage(e: any) {
                    if (e.event === 'log') {
                        if (e.data) {
                            logHandler(JSON.parse(e.data));
                        }
                    } else if (e.event === 'finish') {
                        let updated: boolean = false;
                        if (e.data) {
                            ({ updated } = JSON.parse(e.data));
                        }
                        resolve(updated ? 'updated' : 'cached');
                    } else if (e.event === 'error') {
                        reject(new Error(e.data));
                    }
                },
                onerror(err) {
                    ctrl.abort();
                    reject(err);
                    throw err; // rethrow to stop the operation
                },
            });
        });
    }

    async getBaseUrl(
        oldBaseUrl: string,
        entityId: CompoundEntityRef,
        path: string,
    ): Promise<string> {
        const { kind, namespace, name } = entityId;

        const apiOrigin = await this.getApiOrigin();
        let newBaseUrl = `${apiOrigin}/static/docs/${namespace}/${kind}/${name}/${path}`;

        if (inPreviewMode()) {
            newBaseUrl = `${apiOrigin}/static/docs/_previews/${name}/${getPreviewBranchName()}/${path}`;
        }

        return new URL(
            oldBaseUrl,
            newBaseUrl.endsWith('/') ? newBaseUrl : `${newBaseUrl}/`,
        ).toString();
    }
}

export const customTechDocsApis: AnyApiFactory[] = [
    createApiFactory({
        api: techdocsStorageApiRef,
        deps: {
            configApi: configApiRef,
            discoveryApi: discoveryApiRef,
            fetchApi: fetchApiRef,
        },
        factory: ({ configApi, discoveryApi, fetchApi }) =>
            new TechDocsStorageClient({
                configApi,
                discoveryApi,
                fetchApi,
            }),
    }),
    createApiFactory({
        api: techdocsApiRef,
        deps: {
            configApi: configApiRef,
            discoveryApi: discoveryApiRef,
            fetchApi: fetchApiRef,
        },
        factory: ({ configApi, discoveryApi, fetchApi }) =>
            new TechDocsClient({
                configApi,
                discoveryApi,
                fetchApi,
            }),
    }),
];

export function inPreviewMode(): boolean {
    if (window.location.hash !== "" && window.location.hash.includes('?')) {
      const params = new URLSearchParams("?" + window.location.hash.split('?')[1]);
      return params.has('previewBranch');
    }

    const params = new URLSearchParams(window.location.search);
    return params.has('previewBranch');
}

export function getPreviewBranchName(): string|null {
    if (window.location.hash !== "" && window.location.hash.includes('?')) {
      const params = new URLSearchParams("?" + window.location.hash.split('?')[1]);
      return params.get('previewBranch');
    }

    const params = new URLSearchParams(window.location.search);
    return params.get('previewBranch');
}
